/**
 * #2893 (Codex P1) — turn-aware supersession of locally-minted chat rows by
 * their pod-journal twins.
 *
 * On the desktop cloud path a turn can exist in TWO id-spaces: the renderer's
 * local rows (`user-<ts>` / `ai-<ts+1>`, minted by `startTurn`, persisted to
 * chat-history.json by the stream-persistence handle) and the journal
 * projection's rows. An id-only merge keeps both — duplicate bubbles — in the
 * silent-completion path #2893 fixes AND at desktop hydration once
 * `agent:history` is pod-routed.
 *
 * crucible#2902 CLOSED the id-space gap for the common case: `agent.send@1` now
 * forwards the renderer's `turnId` and the pod journal ADOPTS it for the opening
 * turn (`agent-send.ts` cloud branch → the `index.ts` dispatch closure →
 * `cloud-agent-dispatcher.ts` → `agent-send-phase4-driver.ts` →
 * `agent-journal-tee.ts` `openRun`), so the two id-spaces share a `turnId`. Only
 * legacy pairings (old GUI ↔ new pod, new GUI ↔ old pod) still diverge — there
 * the journal tee mints its own id via `genId()` and the text fallback applies.
 *
 * This module decides which EXISTING rows are superseded by an incoming
 * journal projection so the merge can drop them and keep the journal copy
 * (journal ids are durable — they re-appear identically on every later
 * projection, so keeping them makes all future merges converge by plain id
 * dedupe; keeping the local ids instead would need a persistent alias table).
 *
 * ## Primary mechanism: shared turnId (crucible#2902)
 *
 * The cloud dispatch NOW forwards the renderer's `turnId` and the pod journal
 * adopts it for the run's opening turn (`agent-send.ts` cloud branch →
 * `cloud-agent-dispatcher.ts` → `agent.send@1` `turnId` param →
 * `agent-send-phase4-driver.ts` → `agent-journal-tee.ts` openRun). So a
 * projected journal turn and its local renderer twin share a `turnId` BY
 * CONSTRUCTION, and supersession is exact: the local rows of any turn whose
 * `turnId` matches a NEW projected turn are superseded (same candidacy as the
 * text path — a live-streaming or errored turn is never dropped). This is
 * deterministic and immune to the failure mode the text heuristic degrades on
 * (an in-pod prompt rewrite / uploaded-images note changed the journalled text
 * and left a visible duplicate).
 *
 * The text-uniqueness invariant below remains the COMPATIBILITY FALLBACK for
 * old GUIs / old pods that don't share a turnId (new-GUI↔old-pod: the pod minted
 * its own turnId; old-GUI↔new-pod: no `turnId` forwarded ⇒ `genId()`).
 *
 * ## Uniqueness invariant (Codex round-4) — old-pod/old-GUI fallback
 *
 * A supersession requires a UNIQUE text↔text bijection; repeated prompts
 * never supersede. Concretely, for a distinct user text `t`, supersession
 * fires only when ALL of:
 *   - exactly ONE new projected turn with text `t` in the incoming page
 *     (both rows present, neither id already in the timeline);
 *   - exactly ONE eligible local candidate with text `t` in the timeline;
 *   - NO live-streaming local turn with text `t`.
 * Any multiplicity on either side fails CLOSED: nothing is superseded for
 * that text — the journal turn appends (a visible duplicate) and the
 * timeline converges for real once crucible#2902 forwards the renderer ids.
 *
 * Why so strict: the journal tee is best-effort, so both the projection and
 * the local rows are gapped subsequences of the true turn sequence. Three
 * review rounds showed every pairing heuristic over repeated identical text
 * mis-aligns under some gap window — greedy oldest-first claimed the wrong
 * turn outright, and even count-matched ordinal pairing breaks on a SHIFTED
 * window (local turns 3,4 "continue" vs a journal page carrying turns 4,5:
 * equal counts, wrong correspondence, turn 3 deleted). Uniqueness is the
 * only correspondence signal that cannot shift, so it kills the class — no
 * counting, no windows, no pairing heuristics left to be wrong. The safe
 * failure is always a visible duplicate, never a deleted different turn.
 *
 * Candidacy (Codex round-4 P2): an ERRORED local attempt (assistant carries
 * a structured `error`) or an ORPHANED user row (no adjacent
 * paired assistant before the next user row) is
 * excluded from candidacy entirely — it does not count toward uniqueness,
 * is never superseded, and keeps rendering. A failed-then-retried prompt
 * therefore still converges: the errored attempt stays visible, and the
 * single CLEAN local candidate uniquely matches the single projected turn.
 * A LIVE-streaming local turn (`protectAssistantId`) is different: it is a
 * plausible twin of the projected turn itself, so it counts as ambiguity
 * and VETOES supersession for its text (excluding it from the count would
 * re-enable deleting a different same-text turn while its real twin is the
 * one still streaming).
 *
 * Matching keys on the user text because it is the verbatim `message` the
 * renderer itself sent over the wire — an echo of a forwarded field
 * (byte-exact through state.ts → agent-send.ts → the cloud dispatcher → the
 * in-pod driver → the journal tee), not a semantic guess.
 *
 * Deliberately NOT matched on assistant content: the live reducer accretes
 * every text chunk (including sub-agent text) into `content`, while the
 * journal projection excludes attributed text and `turn.finalised` overwrites
 * with the SDK's final join — the two copies of a HEALTHY turn are not
 * byte-comparable, and requiring a content match would reintroduce the
 * duplicate for every build turn that used sub-agents.
 *
 * FRAGILITY NOTE (load-bearing hook ordering): when a projected turn is NOT
 * superseded (uniqueness fail-closed, live veto), the reducer merge APPENDS
 * the journal twin — permanently. Once its ids are in the timeline the twin
 * counts as already-converged and no later merge removes it. The reason this
 * doesn't duplicate live turns in practice is that `useChatSession`'s poll
 * only merges while the originator is NOT locally streaming (the in-flight
 * branch's gate: skip when `currentAiMsgId` names a renderer-minted
 * placeholder — #2949 relaxed the old `currentAiMsgId === null` form so a
 * JOURNAL-backed adopted row, which has no live stream and shares the
 * journal's id-space, no longer blocks an observer's merges — plus
 * reconcile-BEFORE-merge ordering on the idle branch). Any new merge call
 * site that runs while a local turn is streaming will append its journal
 * twin and it will stick.
 */
import type { ChatMessage } from './types';

/**
 * crucible#2902 — the DURABLE supersession path: ids of local rows superseded by
 * a projected journal turn that shares their `turnId`.
 *
 * Once the cloud dispatch forwards the renderer's `turnId` and the pod journal
 * adopts it (`agent-send.ts` → `cloud-agent-dispatcher.ts` → `agent.send@1`
 * `turnId` → `agent-send-phase4-driver.ts` → `agent-journal-tee.ts` openRun), a
 * projected journal turn and its local renderer twin share a `turnId` BY
 * CONSTRUCTION. That shared id is an EXACT correspondence — it cannot shift or
 * mis-align the way matching on repeated/rewritten user text can (the #2902
 * degradation: an in-pod uploaded-images note changed the journalled text and
 * the heuristic kept a visible duplicate). Supersede the local rows of any turn
 * whose `turnId` matches a NEW projected turn (both ids absent from the timeline
 * → not already merged by id), keeping the journal copy (its durable ids
 * converge every later merge by plain id).
 *
 * Candidacy mirrors the text path exactly: a turn with a LIVE-streaming
 * (`protectAssistantId`) or ERRORED assistant row is NEVER superseded — either
 * vetoes the whole turn, so we fail to a visible duplicate, never a wrong
 * delete. A local row that is itself part of the projection (its id already
 * journal-owned) is not a local twin.
 *
 * Pure; `byTurn` is the projection already grouped by `turnId`.
 */
function supersedeBySharedTurnId(
  existing: readonly ChatMessage[],
  byTurn: ReadonlyMap<string, { user?: ChatMessage; assistant?: ChatMessage }>,
  existingIds: ReadonlySet<string>,
  projectedIds: ReadonlySet<string>,
  protectAssistantId: string | null,
): Set<string> {
  const superseded = new Set<string>();
  const newProjectedTurnIds = new Set<string>();
  for (const [turnId, slot] of byTurn) {
    // Require a COMPLETE projected turn — BOTH rows present — exactly like the
    // text path below (`t.user === undefined || t.assistant === undefined`).
    // A gapped page (only the user or only the assistant half — a best-effort
    // `turn.user` miss, or a cursor page boundary that splits the pair) must
    // NOT supersede the full local twin and leave only the partial journal half
    // on screen, dropping the other bubble until a later poll repairs it — or
    // permanently if that half never arrives. Fail to a visible duplicate.
    if (slot.user === undefined || slot.assistant === undefined) continue;
    // A turn whose ids are already in the timeline was merged before — the
    // id-based merge owns it from here.
    if (existingIds.has(slot.user.id) || existingIds.has(slot.assistant.id)) continue;
    newProjectedTurnIds.add(turnId);
  }
  if (newProjectedTurnIds.size === 0) return superseded;

  const localRowsByTurn = new Map<string, ChatMessage[]>();
  for (const m of existing) {
    if (m.turnId === undefined || !newProjectedTurnIds.has(m.turnId)) continue;
    if (projectedIds.has(m.id)) continue; // a journal row already in the timeline is not a local twin
    const rows = localRowsByTurn.get(m.turnId) ?? [];
    rows.push(m);
    localRowsByTurn.set(m.turnId, rows);
  }
  for (const rows of localRowsByTurn.values()) {
    const vetoed = rows.some(
      (r) => r.id === protectAssistantId || (r.role === 'assistant' && r.error !== undefined),
    );
    if (vetoed) continue;
    for (const r of rows) superseded.add(r.id);
  }
  return superseded;
}

/**
 * Compute the ids of existing timeline rows superseded by the incoming
 * journal projection. Pure; no mutation. Shared-`turnId` supersession
 * (crucible#2902) runs first; the user-text uniqueness invariant (see the
 * module doc) is the old-GUI/old-pod fallback.
 */
export function findSupersededTimelineRows(
  existing: readonly ChatMessage[],
  projected: readonly ChatMessage[],
  protectAssistantId: string | null = null,
): Set<string> {
  const superseded = new Set<string>();
  if (existing.length === 0 || projected.length === 0) return superseded;

  const existingIds = new Set(existing.map((m) => m.id));
  const projectedIds = new Set(projected.map((m) => m.id));

  // Group the projection into turns (user + assistant sharing a turnId) and
  // count the NEW ones per user text. A turn whose ids are already in the
  // timeline was merged before — the id-based merge owns it from here.
  const byTurn = new Map<string, { user?: ChatMessage; assistant?: ChatMessage }>();
  for (const m of projected) {
    if (m.turnId === undefined) continue;
    const slot = byTurn.get(m.turnId) ?? {};
    if (m.role === 'user' && slot.user === undefined) slot.user = m;
    else if (m.role === 'assistant' && slot.assistant === undefined) slot.assistant = m;
    byTurn.set(m.turnId, slot);
  }

  // crucible#2902 — DETERMINISTIC turnId supersession runs FIRST (the durable
  // fix); the text-uniqueness pass below is now the old-GUI/old-pod fallback.
  for (const id of supersedeBySharedTurnId(
    existing,
    byTurn,
    existingIds,
    projectedIds,
    protectAssistantId,
  )) {
    superseded.add(id);
  }

  const projectedTextCounts = new Map<string, number>();
  for (const t of byTurn.values()) {
    if (t.user === undefined || t.assistant === undefined) continue;
    if (existingIds.has(t.user.id) || existingIds.has(t.assistant.id)) continue;
    projectedTextCounts.set(t.user.content, (projectedTextCounts.get(t.user.content) ?? 0) + 1);
  }
  if (projectedTextCounts.size === 0) return superseded;

  /** Paired assistant row for an existing user row: same turnId when both
   *  carry one (precise — ids don't lie), else the ADJACENT next assistant:
   *  the first assistant row after the user row that appears BEFORE any
   *  subsequent user row (Codex round-5 P1: an unbounded "next assistant
   *  anywhere later" fallback let an orphaned same-text user row steal a
   *  clean retry's assistant, making the orphan the unique candidate and
   *  deleting the WRONG rows). Non-user/non-assistant rows (checkpoints)
   *  do not break adjacency. A user row with no adjacent assistant is an
   *  orphan — excluded from candidacy, never superseded. */
  const pairedAssistantIndex = (userIdx: number): number => {
    const u = existing[userIdx]!;
    if (u.turnId !== undefined) {
      for (let j = 0; j < existing.length; j++) {
        const m = existing[j]!;
        if (m.role === 'assistant' && m.turnId === u.turnId) return j;
      }
    }
    for (let j = userIdx + 1; j < existing.length; j++) {
      const m = existing[j]!;
      if (m.role === 'assistant') return j;
      if (m.role === 'user') return -1; // adjacency bound — orphan
    }
    return -1;
  };

  // Classify the existing local turns per user text:
  //   candidates — clean local turns, superseded-able;
  //   live       — live-streaming turns, which VETO their text (ambiguity);
  //   excluded   — errored / orphaned / journal-backed rows: they render,
  //                are never superseded, and never count toward uniqueness.
  const candidatesByText = new Map<string, Array<{ userIdx: number; assistantIdx: number }>>();
  const liveTexts = new Set<string>();
  const claimedAssistantIndices = new Set<number>();
  for (let i = 0; i < existing.length; i++) {
    const u = existing[i]!;
    if (u.role !== 'user') continue;
    if (!projectedTextCounts.has(u.content)) continue;
    // A row that is itself part of the projection is never a local twin.
    if (projectedIds.has(u.id)) continue;
    const rawAssistantIdx = pairedAssistantIndex(i);
    // Guard against two user rows resolving to the same fallback assistant.
    const assistantIdx =
      rawAssistantIdx >= 0 && !claimedAssistantIndices.has(rawAssistantIdx) ? rawAssistantIdx : -1;
    if (assistantIdx >= 0) claimedAssistantIndices.add(assistantIdx);
    const pair = assistantIdx >= 0 ? existing[assistantIdx] : undefined;
    if (pair === undefined) continue; // orphaned attempt — excluded from candidacy
    if (pair.error !== undefined) continue; // errored attempt — excluded from candidacy
    if (projectedIds.has(pair.id)) continue; // journal-backed — excluded
    if (pair.id === protectAssistantId) {
      liveTexts.add(u.content);
      continue;
    }
    const list = candidatesByText.get(u.content) ?? [];
    list.push({ userIdx: i, assistantIdx });
    candidatesByText.set(u.content, list);
  }

  for (const [text, projectedCount] of projectedTextCounts) {
    // Uniqueness invariant: exactly one new projected turn, exactly one
    // clean local candidate, no live-streaming turn with this text. Any
    // multiplicity fails closed (visible duplicate, never a wrong delete).
    if (projectedCount !== 1) continue;
    const candidates = candidatesByText.get(text) ?? [];
    if (candidates.length !== 1) continue;
    if (liveTexts.has(text)) continue;
    const c = candidates[0]!;
    superseded.add(existing[c.userIdx]!.id);
    superseded.add(existing[c.assistantIdx]!.id);
  }
  return superseded;
}
